/* eslint-disable max-lines */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

// project imports
import axiosex from "axios";
import { dispatch } from "../index";

const buildQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, value);
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : "";
};

const createEmptyMarketingActivityStats = () => ({
  range: "daily",
  startDate: null,
  endDate: null,
  totalAttempts: 0,
  uniqueParticipants: 0,
  rewardRecipients: 0,
  rewardsPaid: 0,
  totalRewardValue: 0,
});

const createEmptyUserMarketingActivityHistory = () => ({
  userId: null,
  items: [],
  totalCashRewards: 0,
  totalPointRewards: 0,
});

const createEmptyUserPlayHistory = () => ({
  transactions: [],
  pagination: { total: 0, limit: 50, offset: 0 },
  filters: { limit: 50, offset: 0, source: "ALL" },
});

const normalizePlayHistoryTransaction = (
  transaction,
  fallbackUserId = null,
) => {
  const balanceBefore = Number(
    transaction?.balanceBefore ?? transaction?.before_wallet ?? 0,
  );
  const balanceAfter = Number(
    transaction?.balanceAfter ?? transaction?.after_wallet ?? 0,
  );
  const explicitAmount = Number(transaction?.amount);
  const derivedAmount = Number.isNaN(explicitAmount)
    ? balanceAfter - balanceBefore
    : explicitAmount;
  const createdAt = transaction?.createdAt || transaction?.created_at || null;
  const createdUnix = createdAt
    ? Math.floor(new Date(createdAt).getTime() / 1000) || 0
    : Number(transaction?.created_unix || 0);

  return {
    ...transaction,
    id: transaction?.id || transaction?._id || transaction?.ambTxnId,
    note:
      transaction?.note ||
      transaction?.playInfo ||
      [transaction?.source, transaction?.type].filter(Boolean).join(" ") ||
      "-",
    amount: derivedAmount,
    before_wallet: balanceBefore,
    after_wallet: balanceAfter,
    created_at: createdAt,
    created_unix: createdUnix,
    user_id: transaction?.user_id || transaction?.userId || fallbackUserId,
    movementSource: transaction?.source || "ALL",
    movementStatus: transaction?.status || "-",
    movementTypeLabel: transaction?.type || "-",
  };
};

// ----------------------------------------------------------------------

const initialState = {
  error: null,
  allProvider: [],
  rankPromoteMonthly: [],
  rankPromote: [],
  sumReceiveMonthly: [],
  sumReceive: [],
  allRankingHistory: [],
  positionSummary: [],
  allRankingHistoryByID: [],
  allRanking: [],
  playerCash: [],
  outstandingByID: null,
  freespinByID: [],
  playHistoryByID: [],
  creditHistory: [],
  creditHistoryByID: [],
  withdrawHistoryByID: [],
  withdrawDetailByID: [],
  openBoxSetting: [],
  allBanner: [],
  allEvent: [],
  marketingActivities: [],
  marketingActivityDetail: null,
  marketingActivityStats: createEmptyMarketingActivityStats(),
  userMarketingActivityHistory: createEmptyUserMarketingActivityHistory(),
  idEvent: [],
  allCampaign: [],
  campaignByID: [],
  idBanner: [],
  idPopup: [],
  allPopup: [],
  adminHistoryData: [],
  transactionDepositSummary: [],
  transactionWithdrawSummary: [],
  TMNTransactionHistory: [],
  BANKTransactionHistory: [],
  SMSTransactionHistory: [],
  TMNWebhookTransactionHistory: [],
  SMSBalance: [],
  GSBTransactionHistory: [],
  userAvailablePromotion: [],
  playerLoginLink: [],
  playerBalance: [],
  todayPromotionReceived: [],
  allPromotionReceived: [],
  userPlayHistory: createEmptyUserPlayHistory(),
  userCheckinHistory: {
    items: [],
    pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
  },
  bankList: [],
  transferBankList: [],
  tmnTransferList: [],
  withdrawHistory: [],
  transactionDepositHistory: [],
  withdrawrequestlist: [],
  newUserDepositDaily: [],
  sumAllWithdrawDaily: [],
  sumAllDepositDaily: [],
  sumAllWithdrawMonthly: [],
  sumAllDepositMonthly: [],
  sumAllWithdrawYearly: [],
  sumAllDepositYearly: [],
  sumAllBonusDaily: [],
  sumAllBonusMonthly: [],
  depositHistoryByID: [],
  betflixStatus: [],
  inviteSetting: [],
  tmnSetting: [],
  wheelHistory: [],
  playerWheelHistory: [],
  wheelSetting: [],
  scbSetting: [],
  gsbSetting: [],
  websiteSetting: [],
  tmnData: [],
  scbData: [],
  gsbData: [],
  transactionHistory: [],
  cashbackSetting: [],
  telegramSetting: [],
  withdrawSetting: [],
  totalUsers: [],
  singleUsers: [],
  agentInfo: [],
  agentฺBalance: [],
  singleAdmin: [],
  singlePromo: [],
  allPromo: [],
  singleCreditCode: [],
  allCreditCode: [],
  usedCreditCode: [],
  unusedCreditCode: [],
  todayUsers: [],
  monthUsers: [],
  byIdTransDW: [],
  allTransDW: [],
  allAdmin: [],
  usersS1: [],
  usersS2: [],
  paymentBalance: null,
  bibbyxBalance: null,
  onpayMerchantBalance: null,
  singleMemberReport: [],
};

const slice = createSlice({
  name: "user",
  initialState,
  reducers: {
    // HAS ERROR
    hasError(state, action) {
      state.error = action.payload;
    },

    getSingleMemberReportSuccess(state, action) {
      state.singleMemberReport = action.payload;
    },

    getAllProviderSuccess(state, action) {
      state.allProvider = action.payload;
    },

    rankPromoteMonthlySuccess(state, action) {
      state.rankPromoteMonthly = action.payload;
    },
    rankPromoteSuccess(state, action) {
      state.rankPromote = action.payload;
    },
    sumReceiveMonthlySuccess(state, action) {
      state.sumReceiveMonthly = action.payload;
    },
    sumReceiveSuccess(state, action) {
      state.sumReceive = action.payload;
    },

    getPositionSummarySuccess(state, action) {
      state.positionSummary = action.payload;
    },

    getAllRankingHistorySuccess(state, action) {
      state.allRankingHistory = action.payload;
    },

    getAllRankingHistoryByIDSuccess(state, action) {
      state.allRankingHistoryByID = action.payload;
    },

    getAllRankingSuccess(state, action) {
      state.allRanking = action.payload;
    },

    getPlayerCashSuccess(state, action) {
      state.playerCash = action.payload;
    },

    getOutstandingByIDSuccess(state, action) {
      state.outstandingByID = action.payload;
    },

    getFreespinByIDSuccess(state, action) {
      state.freespinByID = action.payload;
    },

    getPlayHistoryByIDSuccess(state, action) {
      state.playHistoryByID = action.payload;
    },

    getCreditHistorySuccess(state, action) {
      state.creditHistory = action.payload;
    },

    getCreditHistoryByIDSuccess(state, action) {
      state.creditHistoryByID = action.payload;
    },

    getWithdrawHistoryByIDSuccess(state, action) {
      state.withdrawHistoryByID = action.payload;
    },

    getWithdrawDetailByIDSuccess(state, action) {
      state.withdrawDetailByID = action.payload;
    },

    getAllCampaignSuccess(state, action) {
      state.allCampaign = action.payload;
    },

    getCampaignByIDSuccess(state, action) {
      state.campaignByID = action.payload;
    },

    getAllOpenBoxSettingSuccess(state, action) {
      state.openBoxSetting = action.payload;
    },

    getAllBannerSuccess(state, action) {
      state.allBanner = action.payload;
    },

    getBannerByIDSuccess(state, action) {
      state.idBanner = action.payload;
    },

    getAllEventSuccess(state, action) {
      state.allEvent = action.payload;
    },

    getEventByIDSuccess(state, action) {
      state.idEvent = action.payload;
    },

    getMarketingActivitiesSuccess(state, action) {
      state.marketingActivities = action.payload;
    },

    getMarketingActivityByIdSuccess(state, action) {
      state.marketingActivityDetail = action.payload;
    },

    getMarketingActivityStatsSuccess(state, action) {
      state.marketingActivityStats =
        action.payload || createEmptyMarketingActivityStats();
    },

    getUserMarketingActivityHistorySuccess(state, action) {
      state.userMarketingActivityHistory =
        action.payload || createEmptyUserMarketingActivityHistory();
    },

    getAllPopupSuccess(state, action) {
      state.allPopup = action.payload;
    },

    getPopupByIDSuccess(state, action) {
      state.idPopup = action.payload;
    },

    getAdminHistoryDataSuccess(state, action) {
      state.adminHistoryData = action.payload;
    },

    getTransactionDepositSummarySuccess(state, action) {
      state.transactionDepositSummary = action.payload;
    },

    getTransactionWithdrawSummarySuccess(state, action) {
      state.transactionWithdrawSummary = action.payload;
    },

    getTMNTransactionHistorySuccess(state, action) {
      state.TMNTransactionHistory = action.payload;
    },

    getBANKTransactionHistorySuccess(state, action) {
      state.BANKTransactionHistory = action.payload;
    },

    getSMSTransactionHistorySuccess(state, action) {
      state.SMSTransactionHistory = action.payload;
    },

    getTMNWebhookTransactionHistorySuccess(state, action) {
      state.TMNWebhookTransactionHistory = action.payload;
    },

    getSMSBalanceSuccess(state, action) {
      state.SMSBalance = action.payload;
    },

    getGSBTransactionHistorySuccess(state, action) {
      state.GSBTransactionHistory = action.payload;
    },

    getUserAvailablePromotionSuccess(state, action) {
      state.userAvailablePromotion = action.payload;
    },

    getPlayerLoginLinkSuccess(state, action) {
      state.playerLoginLink = action.payload;
    },

    getPlayerBalanceSuccess(state, action) {
      state.playerBalance = action.payload;
    },

    getTodayPromotionReceivedSuccess(state, action) {
      state.todayPromotionReceived = action.payload;
    },

    getAllPromotionReceivedSuccess(state, action) {
      state.allPromotionReceived = action.payload;
    },

    getUserPlayHistorySuccess(state, action) {
      state.userPlayHistory = {
        ...createEmptyUserPlayHistory(),
        ...action.payload,
      };
    },

    getUserCheckinHistorySuccess(state, action) {
      state.userCheckinHistory = action.payload;
    },

    getNewUserDepositDailySuccess(state, action) {
      state.newUserDepositDaily = action.payload;
    },

    getSumAllWithdrawDailySuccess(state, action) {
      state.sumAllWithdrawDaily = action.payload;
    },

    getTMNTransferListSuccess(state, action) {
      state.tmnTransferList = action.payload;
    },

    getSumAllDepositDailySuccess(state, action) {
      state.sumAllDepositDaily = action.payload;
    },

    getSumAllWithdrawMonthlySuccess(state, action) {
      state.sumAllWithdrawMonthly = action.payload;
    },

    getSumAllDepositMonthlySuccess(state, action) {
      state.sumAllDepositMonthly = action.payload;
    },

    getSumAllWithdrawYearlySuccess(state, action) {
      state.sumAllWithdrawYearly = action.payload;
    },

    getSumAllDepositYearlySuccess(state, action) {
      state.sumAllDepositYearly = action.payload;
    },

    getSumAllBonusDailySuccess(state, action) {
      state.sumAllBonusDaily = action.payload;
    },

    getSumAllBonusMonthlySuccess(state, action) {
      state.sumAllBonusMonthly = action.payload;
    },

    getWithdrawHistorySuccess(state, action) {
      state.withdrawHistory = action.payload;
    },
    // GET USERS STYLE 1
    getTransactionHistorySuccess(state, action) {
      state.transactionHistory = action.payload;
    },

    getTransactionDepositHistorySuccess(state, action) {
      state.transactionDepositHistory = action.payload;
    },

    getWithdrawrequestlistSuccess(state, action) {
      state.withdrawrequestlist = action.payload;
    },

    getDepositHistoryByIDSuccess(state, action) {
      state.depositHistoryByID = action.payload;
    },

    getBetflixStatus(state, action) {
      state.betflixStatus = action.payload;
    },

    getBankListSuccess(state, action) {
      state.bankList = action.payload;
    },

    getTransferBankListSuccess(state, action) {
      state.transferBankList = action.payload;
    },

    getTMNSettingSuccess(state, action) {
      state.tmnSetting = action.payload;
    },

    getWheelHistorySuccess(state, action) {
      state.wheelHistory = action.payload;
    },

    getPlayerWheelHistorySuccess(state, action) {
      state.playerWheelHistory = action.payload;
    },

    getWheelSettingSuccess(state, action) {
      state.wheelSetting = action.payload;
    },

    getSCBSettingSuccess(state, action) {
      state.scbSetting = action.payload;
    },

    getGSBSettingSuccess(state, action) {
      state.gsbSetting = action.payload;
    },

    getTMNDataSuccess(state, action) {
      state.tmnData = action.payload;
    },

    getSCBDataSuccess(state, action) {
      state.scbData = action.payload;
    },

    getGSBDataSuccess(state, action) {
      state.gsbData = action.payload;
    },

    getWebsiteSettingSuccess(state, action) {
      state.websiteSetting = action.payload;
    },

    getTelegramSettingSuccess(state, action) {
      state.telegramSetting = action.payload;
    },

    getWithdrawSettingSuccess(state, action) {
      state.withdrawSetting = action.payload;
    },

    getInviteSettingSuccess(state, action) {
      state.inviteSetting = action.payload;
    },

    getCashbackSettingSuccess(state, action) {
      state.cashbackSetting = action.payload;
    },

    getUsedCreditCodeSuccess(state, action) {
      state.usedCreditCode = action.payload;
    },

    getUnusedCreditCodeSuccess(state, action) {
      state.unusedCreditCode = action.payload;
    },

    getAgentInfoSuccess(state, action) {
      state.agentInfo = action.payload;
    },

    getAgentBalanceSuccess(state, action) {
      state.agentBalance = action.payload;
    },

    getAllAdminSuccess(state, action) {
      state.allAdmin = action.payload;
    },

    getUsersListStyle1Success(state, action) {
      state.usersS1 = action.payload;
    },

    getTotalUsersSuccess(state, action) {
      state.totalUsers = action.payload;
    },

    getPromoByIDSuccess(state, action) {
      state.singlePromo = action.payload;
    },

    getAllPromoSuccess(state, action) {
      state.allPromo = action.payload;
    },

    getCreditCodeByIDSuccess(state, action) {
      state.singleCreditCode = action.payload;
    },

    getAllCreditCodeSuccess(state, action) {
      state.allCreditCode = action.payload;
    },

    getUsersByIDSuccess(state, action) {
      state.singleUsers = action.payload;
    },

    getAdminByIDSuccess(state, action) {
      state.singleAdmin = action.payload;
    },

    getTodayUsersSuccess(state, action) {
      state.todayUsers = action.payload;
    },

    getMonthUsersSuccess(state, action) {
      state.monthUsers = action.payload;
    },

    getAllTransactionDWSuccess(state, action) {
      state.allTransDW = action.payload;
    },

    getByIdTransactionDWSuccess(state, action) {
      state.byIdTransDW = action.payload;
    },

    // GET USERS STYLE 2
    getUsersListStyle2Success(state, action) {
      state.usersS2 = action.payload;
    },

    getUserGameHistorySuccess(state, action) {
      state.userGameHistory = action.payload;
    },

    getUserGameHistoryStatsSuccess(state, action) {
      state.userGameHistoryStats = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add cases for pending, fulfilled, and rejected
      .addCase(getPaymentBalance.fulfilled, (state, action) => {
        state.paymentBalance = action.payload;
      })
      .addCase(getPaymentBalance.rejected, (state, action) => {
        state.paymentBalance = null;
        console.error("Payment balance fetch failed:", action.payload);
      })
      .addCase(getBibbyxBalance.fulfilled, (state, action) => {
        state.bibbyxBalance = action.payload;
      })
      .addCase(getBibbyxBalance.rejected, (state, action) => {
        state.bibbyxBalance = null;
        console.error("Bibbyx balance fetch failed:", action.payload);
      })
      .addCase(getOnpayMerchantBalance.fulfilled, (state, action) => {
        state.onpayMerchantBalance = action.payload;
      })
      .addCase(getOnpayMerchantBalance.rejected, (state, action) => {
        state.onpayMerchantBalance = null;
        console.error("Onpay merchant balance fetch failed:", action.payload);
      });
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function getAllProvider(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://space.pgzone88.com/bfx-game-list.json`,
      );
      dispatch(slice.actions.getAllProviderSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSumReceive() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/ranking/sumreceive`,
      );
      dispatch(slice.actions.sumReceiveSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSumReceiveMonthly() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/ranking/sumreceivemonthly`,
      );
      dispatch(slice.actions.sumReceiveMonthlySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getRankPromote() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/ranking/rankpromote`,
      );
      dispatch(slice.actions.rankPromoteSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getRankPromoteMonthly() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/ranking/rankpromotemonthly`,
      );
      dispatch(slice.actions.rankPromoteMonthlySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPositionSummary() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/ranking/positionsummary`,
      );
      dispatch(slice.actions.getPositionSummarySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllRankingHistory() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/ranking/getallrankhistory`,
      );
      dispatch(slice.actions.getAllRankingHistorySuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllRankingHistoryByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/ranking/getrankhistorybyid/${id}`,
      );
      dispatch(slice.actions.getAllRankingHistoryByIDSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllRanking() {
  return async () => {
    try {
      const response = await axiosex.get(`https://api.pgzone88.com/ranking`);
      dispatch(slice.actions.getAllRankingSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPlayerCash(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/userbalance/cash/${id}`,
      );
      dispatch(slice.actions.getPlayerCashSuccess(response?.data?.cashWallet));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getOutstandingByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/userbalance/checkoutstanding/${id}`,
      );
      const payload = response?.data || {};
      const transactions = Array.isArray(payload.transactions)
        ? payload.transactions
        : Array.isArray(payload.data?.transactions)
        ? payload.data.transactions
        : null;

      if (transactions) {
        const openTransactions = transactions.filter((transaction) => {
          const status = String(transaction?.status || "").toUpperCase();
          return !status || status === "OPEN" || status === "PENDING";
        });

        const betAmount = openTransactions.reduce(
          (total, transaction) => total + Number(transaction?.betAmount ?? 0),
          0,
        );
        const deductAmount = openTransactions.reduce((total, transaction) => {
          const explicitDeductAmount = transaction?.deductAmount;
          if (
            explicitDeductAmount !== undefined &&
            explicitDeductAmount !== null
          ) {
            return total + Number(explicitDeductAmount || 0);
          }

          const bet = Number(transaction?.betAmount ?? 0);
          const payout = Number(transaction?.payoutAmount ?? 0);
          return total + Math.max(bet - payout, 0);
        }, 0);

        dispatch(
          slice.actions.getOutstandingByIDSuccess({
            status: "success",
            error_code: null,
            msg: openTransactions.length ? "" : "ไม่มีรายการคงค้าง",
            data: {
              bet_amount: betAmount,
              deduct_amount: deductAmount,
              transactions: openTransactions,
              pagination:
                payload.pagination || payload.data?.pagination || null,
            },
          }),
        );
        return;
      }

      dispatch(
        slice.actions.getOutstandingByIDSuccess({
          status: payload.status || "error",
          error_code: payload.error_code ?? null,
          msg: payload.msg || "",
          data:
            payload.status === "success" && payload.data
              ? {
                  bet_amount: Number(payload.data.bet_amount ?? 0),
                  deduct_amount: Number(payload.data.deduct_amount ?? 0),
                }
              : null,
        }),
      );
    } catch (error) {
      dispatch(
        slice.actions.getOutstandingByIDSuccess({
          status: "error",
          error_code: error?.response?.status ?? null,
          msg:
            error?.response?.data?.msg ||
            error?.response?.data?.message ||
            error?.message ||
            "ไม่สามารถดึงข้อมูลได้",
          data: null,
        }),
      );
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getFreespinByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/userbalance/checkfreespins/${id}`,
      );
      dispatch(slice.actions.getFreespinByIDSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPlayHistoryByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/userbalance/checkhistorygame/${id}`,
      );
      dispatch(slice.actions.getPlayHistoryByIDSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCreditHistory() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/userbalance/allcredithistory`,
      );
      dispatch(slice.actions.getCreditHistorySuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCreditHistoryByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/userbalance/credithistory/${id}`,
      );
      dispatch(slice.actions.getCreditHistoryByIDSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCampaignByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/marketingcampaign/getbyid/${id}`,
      );
      dispatch(slice.actions.getCampaignByIDSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllCampaign() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/marketingcampaign/`,
      );
      dispatch(slice.actions.getAllCampaignSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getMarketingActivities() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/marketing-activity`,
      );
      dispatch(
        slice.actions.getMarketingActivitiesSuccess(response.data?.data || []),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getMarketingActivityById(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/marketing-activity/${id}`,
      );
      dispatch(
        slice.actions.getMarketingActivityByIdSuccess(response.data?.data),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getMarketingActivityStats(range = "daily") {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/marketing-activity/stats`,
        {
          params: { range },
        },
      );
      dispatch(
        slice.actions.getMarketingActivityStatsSuccess(
          response.data?.data || createEmptyMarketingActivityStats(),
        ),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUserMarketingActivityHistory(userId) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/marketing-activity/user/${userId}/history`,
      );
      dispatch(
        slice.actions.getUserMarketingActivityHistorySuccess(
          response.data?.data || createEmptyUserMarketingActivityHistory(),
        ),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createMarketingActivity(payload) {
  return async () => {
    try {
      const response = await axiosex.post(
        `https://api.pgzone88.com/marketing-activity`,
        payload,
      );
      dispatch(
        slice.actions.getMarketingActivityByIdSuccess(response.data?.data),
      );
      dispatch(getMarketingActivities());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateMarketingActivity(id, payload) {
  return async () => {
    try {
      const response = await axiosex.put(
        `https://api.pgzone88.com/marketing-activity/${id}`,
        payload,
      );
      dispatch(
        slice.actions.getMarketingActivityByIdSuccess(response.data?.data),
      );
      dispatch(getMarketingActivities());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function updateMarketingActivityStatus(id, status) {
  return async () => {
    try {
      await axiosex.patch(
        `https://api.pgzone88.com/marketing-activity/${id}/status`,
        { status },
      );
      dispatch(getMarketingActivities());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function deleteMarketingActivity(id) {
  return async () => {
    try {
      await axiosex.delete(`https://api.pgzone88.com/marketing-activity/${id}`);
      dispatch(slice.actions.getMarketingActivityByIdSuccess(null));
      dispatch(getMarketingActivities());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function getOpenBoxSetting() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/countdownbox/setting`,
      );
      dispatch(slice.actions.getAllOpenBoxSettingSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllBanner() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/banner`,
      );
      dispatch(slice.actions.getAllBannerSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getBannerByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/banner/${id}`,
      );
      dispatch(slice.actions.getBannerByIDSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllEvent() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/event`,
      );
      dispatch(slice.actions.getAllEventSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getEventByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/event/${id}`,
      );
      dispatch(slice.actions.getEventByIDSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllPopup() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/popup`,
      );
      dispatch(slice.actions.getAllPopupSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPopupByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/popup/${id}`,
      );
      dispatch(slice.actions.getPopupByIDSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAdminHistoryData() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/189a1sf898f/getadminhistory`,
      );
      dispatch(slice.actions.getAdminHistoryDataSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTMNTransactionHistory() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/bank/tmnhistory`,
      );
      dispatch(
        slice.actions.getTMNTransactionHistorySuccess(response.data.data),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getBANKTransactionHistory() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/bank/bankhistory`,
      );
      dispatch(
        slice.actions.getBANKTransactionHistorySuccess(response.data.data),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSMSBalance() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/sms/balance`,
      );
      dispatch(slice.actions.getSMSBalanceSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSMSTransactionHistory() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/sms/smstransaction`,
      );
      dispatch(
        slice.actions.getSMSTransactionHistorySuccess(response.data.data),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTMNWebhookTransactionHistory() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/bank/tmnwebhookhistory`,
      );
      dispatch(
        slice.actions.getTMNWebhookTransactionHistorySuccess(
          response.data.data,
        ),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getGSBTransactionHistory() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/bank/gsbhistory`,
      );
      dispatch(
        slice.actions.getGSBTransactionHistorySuccess(response.data.data),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUserAvailablePromotion(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/usersystem/getavailablepromotion/${id.id}`,
      );
      dispatch(
        slice.actions.getUserAvailablePromotionSuccess(response.data.data),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPlayerLoginLink(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/gamelogin/${id.id}`,
      );
      dispatch(slice.actions.getPlayerLoginLinkSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPlayerBalance(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/userbalance/balance/${id.id}`,
      );
      dispatch(slice.actions.getPlayerBalanceSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTodayPromotionReceived(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/usersystem/checkreceivedpromotion/${id.id}`,
      );
      dispatch(
        slice.actions.getTodayPromotionReceivedSuccess(response.data.data),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllPromotionReceived(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/usersystem/checkallreceivedpromotion/${id.id}`,
      );
      dispatch(
        slice.actions.getAllPromotionReceivedSuccess(response.data.data),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUserPlayHistory(idOrOptions) {
  return async () => {
    const options =
      typeof idOrOptions === "object" && idOrOptions !== null
        ? idOrOptions
        : { id: idOrOptions };
    const { id, limit = 50, offset = 0, source = "ALL" } = options;

    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/agentbalance/playsummary/${id}`,
        {
          params: {
            limit,
            offset,
            source,
          },
        },
      );
      const payload = response?.data?.data || {};
      const transactions = Array.isArray(payload?.transactions)
        ? payload.transactions.map((transaction) =>
            normalizePlayHistoryTransaction(transaction, id),
          )
        : [];
      dispatch(
        slice.actions.getUserPlayHistorySuccess({
          transactions,
          pagination: {
            total: Number(
              payload?.pagination?.total || transactions.length || 0,
            ),
            limit: Number(payload?.pagination?.limit || limit || 50),
            offset: Number(payload?.pagination?.offset || offset || 0),
          },
          filters: {
            limit: Number(limit || 50),
            offset: Number(offset || 0),
            source: String(source || "ALL").toUpperCase(),
          },
        }),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      dispatch(
        slice.actions.getUserPlayHistorySuccess(createEmptyUserPlayHistory()),
      );
    }
  };
}

export function getUserCheckinHistory({ userId, page = 1, limit = 20 } = {}) {
  return async () => {
    if (!userId) {
      dispatch(
        slice.actions.getUserCheckinHistorySuccess({
          items: [],
          pagination: { page: 1, limit, total: 0, totalPages: 0 },
        }),
      );
      return;
    }

    const params = new URLSearchParams();
    if (page) params.append("page", page);
    if (limit) params.append("limit", limit);
    const queryString = params.toString() ? `?${params.toString()}` : "";

    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/checkin/history/${userId}${queryString}`,
      );
      dispatch(
        slice.actions.getUserCheckinHistorySuccess(
          response?.data?.data || {
            items: [],
            pagination: { page, limit, total: 0, totalPages: 0 },
          },
        ),
      );
    } catch (error) {
      dispatch(
        slice.actions.getUserCheckinHistorySuccess({
          items: [],
          pagination: { page, limit, total: 0, totalPages: 0 },
        }),
      );
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getWithdrawDetailByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/userbalance/checkwithdrawdetail/${id}`,
        {
          headers: {
            "Cache-Control":
              "no-cache, no-store, must-revalidate, must-revalidate, post-check=0, pre-check=0",
            Pragma: "no-cache",
            Expires: "0",
          },
        },
      );
      dispatch(slice.actions.getWithdrawDetailByIDSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getNewUserDepositDaily(params = {}) {
  return async () => {
    try {
      const queryString = buildQueryString(params);
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/newuserdepositdaily${queryString}`,
      );
      dispatch(slice.actions.getNewUserDepositDailySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSumAllWithdrawDaily(params = {}) {
  return async () => {
    try {
      const queryString = buildQueryString(params);
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/sumallwithdrawdaily${queryString}`,
      );
      dispatch(slice.actions.getSumAllWithdrawDailySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTMNTransferList() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/bank/tmntransferlist`,
      );
      dispatch(slice.actions.getTMNTransferListSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSumAllDepositDaily(params = {}) {
  return async () => {
    try {
      const queryString = buildQueryString(params);
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/sumalldepositdaily${queryString}`,
      );
      dispatch(slice.actions.getSumAllDepositDailySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSumAllDepositMonthly(params = {}) {
  return async () => {
    try {
      const queryString = buildQueryString(params);
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/sumalldepositmonthly${queryString}`,
      );
      dispatch(
        slice.actions.getSumAllDepositMonthlySuccess(response.data.data),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSumAllWithdrawMonthly(params = {}) {
  return async () => {
    try {
      const queryString = buildQueryString(params);
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/sumallwithdrawmonthly${queryString}`,
      );
      dispatch(
        slice.actions.getSumAllWithdrawMonthlySuccess(response.data.data),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSumAllWithdrawYearly() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/sumallwithdrawyearly`,
      );
      dispatch(
        slice.actions.getSumAllWithdrawYearlySuccess(response.data.data),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSumAllDepositYearly() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/sumalldeposityearly`,
      );
      dispatch(slice.actions.getSumAllDepositYearlySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSumAllBonusDaily(params = {}) {
  return async () => {
    try {
      const queryString = buildQueryString(params);
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/sumallbonusdaily${queryString}`,
      );
      dispatch(slice.actions.getSumAllBonusDailySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSumAllBonusMonthly(params = {}) {
  return async () => {
    try {
      const queryString = buildQueryString(params);
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/sumallbonusmonthly${queryString}`,
      );
      dispatch(slice.actions.getSumAllBonusMonthlySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getBetflixStatus() {
  return async () => {
    try {
      const response = await axiosex.get(`https://api.bfx.fail/v4/status`);
      dispatch(slice.actions.getBetflixStatusSuccess(response.data.status));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getWithdrawHistory(params = {}) {
  return async () => {
    try {
      const queryParams = new URLSearchParams();

      if (params.startDate) {
        queryParams.append("startDate", params.startDate);
      }

      if (params.endDate) {
        queryParams.append("endDate", params.endDate);
      }

      const baseUrl = `https://api.pgzone88.com/userbalance/allwithdraw`;
      const url = queryParams.toString()
        ? `${baseUrl}?${queryParams.toString()}`
        : baseUrl;

      const response = await axiosex.get(url);
      dispatch(slice.actions.getWithdrawHistorySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getWithdrawHistoryByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/userbalance/withdrawhistory/${id}`,
        {
          headers: {
            "Cache-Control":
              "no-cache, no-store, must-revalidate, must-revalidate, post-check=0, pre-check=0",
            Pragma: "no-cache",
            Expires: "0",
          },
        },
      );
      dispatch(slice.actions.getWithdrawHistoryByIDSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getDepositHistoryByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/userbalance/history/${id}`,
      );
      dispatch(slice.actions.getDepositHistoryByIDSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getBankList() {
  return async () => {
    try {
      const response = await axiosex.get(`https://api.pgzone88.com/bank`);
      dispatch(slice.actions.getBankListSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTranferBankList() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/banklist`,
      );
      dispatch(slice.actions.getTransferBankListSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTransactionHistory() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/history`,
      );
      dispatch(slice.actions.getTransactionHistorySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getWithdrawrequestlist() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/withdrawrequestlist`,
        {
          headers: {
            "Cache-Control":
              "no-cache, no-store, must-revalidate, must-revalidate, post-check=0, pre-check=0",
            Pragma: "no-cache",
            Expires: "0",
          },
        },
      );
      dispatch(slice.actions.getWithdrawrequestlistSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTransactionDepositHistory(params = {}) {
  return async () => {
    try {
      const queryParams = new URLSearchParams();

      if (params.startDate) {
        queryParams.append("startDate", params.startDate);
      }

      if (params.endDate) {
        queryParams.append("endDate", params.endDate);
      }

      const queryString = queryParams.toString();
      const baseUrl = `https://api.pgzone88.com/8hwe8vhevv/deposithistory`;
      const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

      const response = await axiosex.get(url);
      dispatch(
        slice.actions.getTransactionDepositHistorySuccess(response.data.data),
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTMNData() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/tmninfo`,
      );
      dispatch(slice.actions.getTMNDataSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSCBData() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/scbinfo`,
      );
      dispatch(slice.actions.getSCBDataSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTMNSetting() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/tmn`,
      );
      dispatch(slice.actions.getTMNSettingSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getWheelHistory() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/wheel/allhistory`,
      );
      dispatch(slice.actions.getWheelHistorySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPlayerWheelHistory(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/wheel/history/${id}`,
      );
      dispatch(slice.actions.getPlayerWheelHistorySuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getWheelSetting() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/wheelsetting`,
      );
      dispatch(slice.actions.getWheelSettingSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getGSBData() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/gsbinfo`,
      );
      dispatch(slice.actions.getGSBDataSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getSCBSetting() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/scb`,
      );
      dispatch(slice.actions.getSCBSettingSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getGSBSetting() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/gsb`,
      );
      dispatch(slice.actions.getGSBSettingSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getWebsiteSetting() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/websetting`,
      );
      dispatch(slice.actions.getWebsiteSettingSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCashbackSetting() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/cashback`,
      );
      dispatch(slice.actions.getCashbackSettingSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTelegramSetting() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/telegram`,
      );
      dispatch(slice.actions.getTelegramSettingSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getWithdrawSetting() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/withdraw`,
      );
      dispatch(slice.actions.getWithdrawSettingSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUsedCreditCode() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/creditcode/used`,
      );
      dispatch(slice.actions.getUsedCreditCodeSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getInviteSetting() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/setting/invite`,
      );
      dispatch(slice.actions.getInviteSettingSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUnusedCreditCode() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/creditcode/unused`,
      );
      dispatch(slice.actions.getUnusedCreditCodeSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllPromo() {
  return async () => {
    try {
      const response = await axiosex.get(`https://api.pgzone88.com/promotion`);
      dispatch(slice.actions.getAllPromoSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAgentInfo() {
  return async () => {
    try {
      const response = await axiosex.get(`https://api.pgzone88.com/agentinfo`);
      dispatch(slice.actions.getAgentInfoSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAgentBalance() {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/agentbalance`,
      );
      dispatch(slice.actions.getAgentBalanceSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getPromoByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/promotion/getbyid/${id}`,
      );
      dispatch(slice.actions.getPromoByIDSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllCreditCode() {
  return async () => {
    try {
      const response = await axiosex.get(`https://api.pgzone88.com/creditcode`);
      dispatch(slice.actions.getAllCreditCodeSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getCreditCodeByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/creditcode/${id.id}`,
      );
      dispatch(slice.actions.getCreditCodeByIDSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUsersByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/usersystem/${id.id}`,
      );
      dispatch(slice.actions.getUsersByIDSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAdminByID(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/189a1sf898f/${id.id}`,
      );
      dispatch(slice.actions.getAdminByIDSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTotalUsers() {
  return async () => {
    try {
      const response = await axiosex.get("https://api.pgzone88.com/totalusers");
      dispatch(slice.actions.getTotalUsersSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getTodayUsers(params = {}) {
  return async () => {
    try {
      const queryString = buildQueryString(params);
      const response = await axiosex.get(
        `https://api.pgzone88.com/todayusers${queryString}`,
      );
      dispatch(slice.actions.getTodayUsersSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getMonthUsers(params = {}) {
  return async () => {
    try {
      const queryString = buildQueryString(params);
      const response = await axiosex.get(
        `https://api.pgzone88.com/todayusers/newusermonthly${queryString}`,
      );
      dispatch(slice.actions.getMonthUsersSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUsersListStyle1() {
  return async () => {
    try {
      const response = await axiosex.get("https://api.pgzone88.com/usersystem");
      dispatch(slice.actions.getUsersListStyle1Success(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllAdmin() {
  return async () => {
    try {
      const response = await axiosex.get(
        "https://api.pgzone88.com/189a1sf898f",
      );
      dispatch(slice.actions.getAllAdminSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getAllTransactionDW() {
  return async () => {
    try {
      const response = await axiosex.get("https://api.pgzone88.com/8hwe8vhevv");
      dispatch(slice.actions.getAllTransactionDWSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getByIdTransactionDW(id) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/8hwe8vhevv/transactionbyid/${id}`,
      );
      dispatch(slice.actions.getByIdTransactionDWSuccess(response.data.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUserGameHistory(userId, params = {}) {
  return async (dispatch) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key]) queryParams.append(key, params[key]);
      });

      const response = await axiosex.get(
        `https://api.pgzone88.com/game-history/user-id/${userId}?${queryParams.toString()}`,
        {
          headers: {
            "Cache-Control":
              "no-cache, no-store, must-revalidate, post-check=0, pre-check=0",
            Pragma: "no-cache",
            Expires: "0",
          },
        },
      );
      dispatch(slice.actions.getUserGameHistorySuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUserGameHistoryStats(userId, days = 30) {
  return async (dispatch) => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/game-history/stats/${userId}?days=${days}`,
        {
          headers: {
            "Cache-Control":
              "no-cache, no-store, must-revalidate, post-check=0, pre-check=0",
            Pragma: "no-cache",
            Expires: "0",
          },
        },
      );
      dispatch(slice.actions.getUserGameHistoryStatsSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// Add new async thunk for payment balance
export const getPaymentBalance = createAsyncThunk(
  "user/getPaymentBalance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosex.post(
        "https://api.pgzone88.com/fairpay/balance",
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch payment balance",
      );
    }
  },
);

export const getBibbyxBalance = createAsyncThunk(
  "user/getBibbyxBalance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosex.post(
        "https://api.pgzone88.com/bibbyx/balance",
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch Bibbyx balance",
      );
    }
  },
);

export const getOnpayMerchantBalance = createAsyncThunk(
  "user/getOnpayMerchantBalance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosex.get(
        "https://api.pgzone88.com/onpay/merchantsbalance",
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch Onpay merchant balance",
      );
    }
  },
);

export function createFairpayCharge(amount, userId, username) {
  return async () => {
    try {
      const response = await axiosex.post(
        "https://api.pgzone88.com/fairpay/create-charge",
        {
          amount,
          userId,
          username,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function checkFairpayStatus(reference) {
  return async () => {
    try {
      const response = await axiosex.get(
        `https://api.pgzone88.com/fairpay/check-status/${reference}`,
      );
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function clearFreespins(id, roundId = null) {
  return async () => {
    try {
      const response = await axiosex.post(
        `https://api.pgzone88.com/userbalance/clearfreespins/${id}`,
        { roundId }, // Send roundId in request body
        {
          headers: {
            "Cache-Control":
              "no-cache, no-store, must-revalidate, must-revalidate, post-check=0, pre-check=0",
            Pragma: "no-cache",
            Expires: "0",
          },
        },
      );
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function loginToGame(userId, provider, gameCode) {
  return async () => {
    try {
      const response = await axiosex.post(
        `https://api.pgzone88.com/gamelogin/gamestart`,
        {
          data: {
            id: userId,
            provider: provider,
            gamecode: gameCode,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function getSingleMemberReport(id, start, end) {
  return async () => {
    try {
      // Normalize to YYYY-MM-DD (slice first 10 if a datetime was provided)
      const toDateOnly = (v) => (v ? String(v).slice(0, 10) : "");
      const s = toDateOnly(start);
      const e = toDateOnly(end) || s;

      const params = new URLSearchParams();
      if (s) params.append("start", s);
      if (e) params.append("end", e);

      const url = `https://api.pgzone88.com/userbalance/singlememberreport/${id}${
        params.toString() ? `?${params.toString()}` : ""
      }`;
      const response = await axiosex.get(url, {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });
      dispatch(slice.actions.getSingleMemberReportSuccess(response.data));
    } catch (error) {
      dispatch(slice.actions.getSingleMemberReportSuccess(null));
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getUserCreditHistoryRange(id, start, end) {
  return async () => {
    try {
      const params = new URLSearchParams();
      if (start) params.append("start", start); // YYYY-MM-DD HH:mm:ss
      if (end) params.append("end", end); // YYYY-MM-DD HH:mm:ss
      const url = `https://api.pgzone88.com/userbalance/credithistoryrange/${id}${
        params.toString() ? `?${params.toString()}` : ""
      }`;
      const response = await axiosex.get(url, {
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });

      const rows = Array.isArray(response?.data?.data)
        ? response.data.data.map((r) => normalizePlayHistoryTransaction(r))
        : [];

      dispatch(
        slice.actions.getUserPlayHistorySuccess({
          transactions: rows,
          pagination: {
            total: rows.length,
            limit: rows.length || 50,
            offset: 0,
          },
        }),
      );
      // Optional: keep server meta in state if needed
      // dispatch(slice.actions.getCreditHistoryByIDSuccess(response.data));
    } catch (error) {
      dispatch(
        slice.actions.getUserPlayHistorySuccess(createEmptyUserPlayHistory()),
      );
      dispatch(slice.actions.hasError(error));
    }
  };
}
